import type { IndustryDemoPack } from '../types'
import { zhLabDemos } from '../lab-demos/zh'

const energy: IndustryDemoPack = {
  demos: {
    eventDriven: zhLabDemos.eventDriven,
    goalDriven: zhLabDemos.goalDriven,
    explainability: zhLabDemos.explainability,
  },
}

const finance: IndustryDemoPack = {
  demos: {
    riskScoring: {
      ...zhLabDemos.riskScoring,
      controlLabel: '为 Alpha 制造公司评分',
      controlHint:
        '调整三个风险维度。Ontology 把加权贡献聚合成单一对手方风险评分,每个数字都能链回背后的规则和数据源。',
      dimensionsTitle: '风险维度',
      dimensions: [
        { id: 'financial', label: '信用历史', weightPct: 40, description: '逾期天数、违约、付款延迟趋势', defaultValue: 5 },
        { id: 'operational', label: '偿付能力', weightPct: 35, description: '现金流覆盖率、杠杆率、利润趋势', defaultValue: 4 },
        { id: 'esg', label: 'KYC 与制裁名单', weightPct: 25, description: 'PEP 筛查、制裁名单、AML 标记历史', defaultValue: 3 },
      ],
      scoreTitle: '对手方风险评分',
      insightTitle: '可审计的信贷决策',
      insightBody:
        '风控委员会几秒内看完"评分 → 规则 → 数据源"的链条。信贷政策一变,所有在途交易自动重评,自带前后差异报告给审计。',
      contextSupplier: {
        label: '在评估的借款人',
        name: 'Alpha 制造公司',
        detail: '8 年信用历史 · $1.2 亿期限贷款 · 行业:工业机械',
      },
    },
    questionGrounded: {
      ...zhLabDemos.questionGrounded,
      insightTitle: '贷款答案,不是贷款文档',
      insightBody:
        '纯 RAG 给你一段话外加三份 PDF 引用。Ontology 给你一条实体链 — KYC 字段、抵押数据、签字权限 — 一路点到源系统。',
      questions: [
        {
          id: 'q-approve-loan',
          question: '本周能给 Alpha 公司放 $1.2 亿期限贷款吗?',
          answer:
            'Alpha 公司是 [[entity:e1|已审核对手方]],信用评分 [[entity:e2|720 分]]。$1.2 亿超过 [[entity:e3|$1 亿 VP 阈值]],根据 [[entity:e4|R-CREDIT-AUTH-04]] 需要 [[entity:e5|VP 委员会会签]]。担保覆盖率 142% 满足 [[entity:e6|R-COLL-130-08]],贷款结构通过 — 等委员会档期。',
          entities: [
            { id: 'e1', label: '已审核对手方', kind: 'object', detail: '2018-03 入库 · 最近 KYC 复审 2024-09 · 12 份历史授信' },
            { id: 'e2', label: '720 分', kind: 'inference', detail: '征信局评分加权内部 24 个月还款表现' },
            { id: 'e3', label: '$1 亿 VP 阈值', kind: 'inference', detail: '汇率换算后权限上限 · 每季度复审' },
            { id: 'e4', label: 'R-CREDIT-AUTH-04', kind: 'constraint', detail: '≥ $1 亿需 VP 委员会 5 人投票 3 票通过', citation: '集团信贷政策 §2.4' },
            { id: 'e5', label: 'VP 委员会会签', kind: 'action', detail: '每周二/周四开会 · 5 个工作日 SLA · 升级路径已记录' },
            { id: 'e6', label: 'R-COLL-130-08', kind: 'constraint', detail: '抵押覆盖 ≥ 130% 才能等同于无抵押风险权重', citation: '集团信贷政策 §6.1' },
          ],
        },
        {
          id: 'q-sector-exposure',
          question: '我们对 Y 行业的全账面敞口有多少?',
          answer:
            'Y 行业总敞口 [[entity:e1|$24 亿]],涉及 [[entity:e2|34 笔活跃授信]]。[[entity:e3|R-CONC-SECTOR-12]] 集中度上限设在 $30 亿,剩余 $6 亿空间。前三大贡献者:[[entity:e4|Alpha ($1.2 亿)]]、[[entity:e5|Gamma ($9500 万)]]、[[entity:e6|Delta ($8000 万)]]。Y 行业新增交易建议进下季度复审。',
          entities: [
            { id: 'e1', label: '$24 亿', kind: 'inference', detail: '汇总活跃授信扣除拨备 · 每晚重算' },
            { id: 'e2', label: '34 笔活跃授信', kind: 'object', detail: '包括贷款、担保、已承诺额度 · 按 NAICS 分类' },
            { id: 'e3', label: 'R-CONC-SECTOR-12', kind: 'constraint', detail: '单行业不超过账面 8% · 触及 7% 触发复审', citation: '风险偏好 §3.2' },
            { id: 'e4', label: 'Alpha ($1.2 亿)', kind: 'object', detail: '12 个月标准续约 · 投资级' },
            { id: 'e5', label: 'Gamma ($9500 万)', kind: 'object', detail: '2025-01 起进入观察名单 · 早期预警标记' },
            { id: 'e6', label: 'Delta ($8000 万)', kind: 'object', detail: '近期延期 · 季度监测' },
          ],
        },
        {
          id: 'q-counterparty',
          question: '为什么对手方 Beta 贸易被标记了?',
          answer:
            'Beta 贸易触发 [[entity:e1|R-EARLY-WARN-07]]: 母公司 Q1 [[entity:e2|盈利下滑 38%]],近 30 天还有 [[entity:e3|两次未按时付息]]。图谱还显示 [[entity:e4|与 Gamma 的共同敞口]](Gamma 同样被标记),集中风险被放大。[[entity:e5|建议动作]]: 收紧约束条款,14 天内要求更新财务报表。',
          entities: [
            { id: 'e1', label: 'R-EARLY-WARN-07', kind: 'constraint', detail: '复合早期预警规则: 母公司业绩冲击 + 漏付 + 相关性', citation: '信贷风险手册 §7' },
            { id: 'e2', label: '盈利下滑 38%', kind: 'inference', detail: 'Q1 公布业绩 vs 同期 · 来源公开披露' },
            { id: 'e3', label: '两次未按时付息', kind: 'object', detail: '债券利息延付 4 天和 9 天 · 总账系统记录' },
            { id: 'e4', label: '与 Gamma 的共同敞口', kind: 'inference', detail: '跨对手方相关性 · 图谱遍历 · Gamma 同被预警' },
            { id: 'e5', label: '建议动作', kind: 'action', detail: '收紧条款 · 14 天要更新财报 · 排信贷官评审' },
          ],
        },
      ],
    },
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: '利率敏感度压力测试阈值 (基点)',
      controlHint:
        '拖动阈值设置容忍区间。Ontology 实时按新阈值重新分类全部组合。',
      thresholdLabel: '容忍度',
      unitLabel: 'bps',
      impactTitle: '对全账面的影响',
      impactSummaryTpl: '{total} 个组合中 {affected} 个翻转区间',
      statusLabels: {
        standard: '容忍内',
        watch: '接近上限',
        sour: '触发复审',
      },
      assetMetaLabel: '利率敏感度',
      insightTitle: '一次利率变动,全组合传播',
      insightBody:
        '过去政策利率一动就是 3 天复审工程,分析师从 4 个系统拉表格。现在 ontology 秒内重评所有组合,标记每一笔在途授信,并自动附上前后差异日志给审计。',
      assets: [
        { id: 'fin-p1', code: 'PORT-A', name: '中小企业工业期限贷款', h2sPpm: 980 },
        { id: 'fin-p2', code: 'PORT-B', name: '商业地产', h2sPpm: 760 },
        { id: 'fin-p3', code: 'PORT-C', name: '消费汽车贷', h2sPpm: 540 },
        { id: 'fin-p4', code: 'PORT-D', name: '贸易融资 · APAC', h2sPpm: 420 },
        { id: 'fin-p5', code: 'PORT-E', name: '欧洲主权债', h2sPpm: 380 },
        { id: 'fin-p6', code: 'PORT-F', name: '投资级企业债', h2sPpm: 280 },
        { id: 'fin-p7', code: 'PORT-G', name: '货币市场', h2sPpm: 180 },
        { id: 'fin-p8', code: 'PORT-H', name: '国债对冲', h2sPpm: 120 },
        { id: 'fin-p9', code: 'PORT-I', name: '短期商票', h2sPpm: 80 },
        { id: 'fin-p10', code: 'PORT-J', name: '回购账本', h2sPpm: 40 },
      ],
      currentThresholdPpm: 500,
      thresholdRange: { min: 50, max: 1200, step: 25 },
    },
  },
}

const healthcare: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...zhLabDemos.eventDriven,
      pendingLabel: '活动告警',
      pendingHint: '一条床边监护仪告警在排队。点 fire,看 ontology 现场合成临床响应。',
      event: {
        code: '告警 C-2104',
        meta: '07:32 · ICU-B · 4 床',
        summary: '患者 B12-Patel · 持续血压下降',
        descriptor: 'BP 110/70 → 78/45 共 8 分钟 · MAP < 65',
        payloadLabel: '生命体征流',
        triggerNodeLabel: 'Event::ALERT_C-2104',
        triggerNodeDetail: '监护仪 CGS-MON-04 · A 级严重度 · 07:32:14 · 已自动消音 2×',
      },
      goals: [
        { id: 'compliance', label: '按协议稳定' },
        { id: 'uptime', label: '诊断与升级' },
        { id: 'cost', label: '分诊与出院' },
      ],
      goalSelectorHint: '同一告警 + 不同目标 → 完全不同的响应链。',
      insightTitle: '床边,不是后台',
      insightBody:
        '链条 — 生命体征、病史、过敏、药物相互作用 — 在告警时刻合成。临床医生 1 分钟决策窗,不用在 4 个系统之间来回切。',
    },
    documentDriven: {
      ...zhLabDemos.documentDriven,
      documentsLabel: '入院文档',
      documentsHint: '点任一份临床文档"上传"。看 LLM 抽实体、ontology 接住、处理链合成。',
      insightTitle: '同一患者,不同输入,同一条链',
      insightBody:
        '分诊、专科匹配、入院前医嘱根据落下来的文档自动合成 — 不需要人工协调员从 EHR / 实验室 / RIS / PACS 拼记录。',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: '转诊单-B12-Patel.pdf',
          meta: '4 页 · 急诊上传',
          summary: '内科转诊 · 68 岁男 · 支架植入术后',
          entities: [
            { kind: '患者', value: 'B12-Patel · 68 岁男' },
            { kind: '急性症状', value: '胸痛 · 活动后气促' },
            { kind: '病史', value: 'STEMI 2023 · LAD 支架' },
            { kind: '过敏', value: '磺胺类 · 严重度 2' },
            { kind: '用药', value: '阿司匹林 · 他汀 · 美托洛尔' },
          ],
        },
        {
          id: 'rfq',
          icon: '🧪',
          name: '实验室-CBC-2104.xml',
          meta: 'HL7 · 检验系统上传',
          summary: '常规 CBC + 代谢全套 · 异常项',
          entities: [
            { kind: '患者', value: 'B12-Patel' },
            { kind: '面板', value: 'CBC + BMP' },
            { kind: '异常', value: 'K = 2.8 mmol/L (低)' },
            { kind: '参考值', value: '3.5-5.0 mmol/L' },
            { kind: '优先级', value: '紧急复核' },
          ],
        },
        {
          id: 'incident',
          icon: '🩻',
          name: 'CT-胸部-2104.dcm',
          meta: 'DICOM · 影像科上传',
          summary: '胸部平扫 CT · 64 层 · 异常占位',
          entities: [
            { kind: '患者', value: 'B12-Patel' },
            { kind: '检查', value: '胸部平扫 CT' },
            { kind: '发现', value: '右肺中叶 2.1 cm 占位' },
            { kind: '后续', value: '建议 PET / 活检' },
            { kind: '对比', value: '无既往影像记录' },
          ],
        },
      ],
    },
    explainability: {
      ...zhLabDemos.explainability,
      insightTitle: '可审计是天生的',
      insightBody:
        '每个临床决策都自带链条 — 指南引用、化验值、过敏、相互作用。防御性医疗变成可辩护医疗:医疗纠纷复盘从图谱开始,而不是病历挖矿。',
      decisions: [
        { id: 'decision-act-viol', question: '"为什么没开药 X?"', goalId: 'compliance', nodeId: 'g1-act' },
        { id: 'decision-c-plan', question: '"为什么转入 ICU?"', goalId: 'compliance', nodeId: 'g1-c-plan' },
        { id: 'decision-c-mat', question: '"为什么标记脓毒症复审?"', goalId: 'compliance', nodeId: 'g1-c-mat' },
        { id: 'decision-cand-viol', question: '"为什么推迟出院?"', goalId: 'compliance', nodeId: 'g1-cand' },
        { id: 'decision-uptime-act', question: '"为什么批准协议外用药?"', goalId: 'uptime', nodeId: 'g2-act' },
      ],
    },
  },
}

const legal: IndustryDemoPack = {
  demos: {
    documentDriven: {
      ...zhLabDemos.documentDriven,
      documentsLabel: '入库法律文档',
      documentsHint: '点任一份文档"上传"。LLM 抽条款,ontology 接义务,审查链合成。',
      insightTitle: '三种文档,三条审查链,同一本体',
      insightBody:
        'NDA / MSA / 监管备案都过同一本体 — 不同条款类型触发不同审查链。新法规一落,所有现存合同自动重新打标。',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'NDA-X 公司-2025.docx',
          meta: '6 页 · 法务部上传',
          summary: '与 X 公司互签 NDA · 3 年期 · IP 重',
          entities: [
            { kind: '当事方', value: 'Acme Inc. ⇄ X Corp' },
            { kind: '期限', value: '36 个月 · 自动续约' },
            { kind: '范围', value: 'IP · 源代码 · 客户名单' },
            { kind: '例外', value: '独立开发 (§4)' },
            { kind: '准据法', value: '特拉华州 · 专属管辖' },
          ],
        },
        {
          id: 'rfq',
          icon: '📋',
          name: 'MSA-Acme-v3.pdf',
          meta: '42 页 · 采购部上传',
          summary: '主服务协议 · Acme 泵业 · 5 年',
          entities: [
            { kind: '当事方', value: 'Acme Inc. ⇄ Acme 泵业' },
            { kind: 'SOW', value: '预计 12 份 · $4000 万上限' },
            { kind: '年额', value: '$800 万 / 年' },
            { kind: '责任', value: '2× 年额 (IP 不封顶)' },
            { kind: '续约', value: '自动续约 · 90 天提前通知' },
          ],
        },
        {
          id: 'incident',
          icon: '⚠',
          name: 'GDPR-DPA-2025-Q2.docx',
          meta: '18 页 · DPO 上传',
          summary: '数据处理附录 · GDPR 一致 · APAC 处理方',
          entities: [
            { kind: '处理方', value: 'Acme APAC Pte. Ltd.' },
            { kind: '次级处理方', value: '3 家 · 2 云 · 1 支持' },
            { kind: '数据', value: 'PII · 交易日志 · KYC' },
            { kind: '传输依据', value: 'SCC + 英国附录' },
            { kind: '审计', value: '年度 · 14 天通知' },
          ],
        },
      ],
    },
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: '隐私法规严格度 (覆盖数据类别数)',
      controlHint:
        '拖动滑块模拟新法规扩展(或放松)受监管数据类别。Ontology 立即对所有合同与流程重新评估。',
      thresholdLabel: '覆盖类别数',
      unitLabel: '类别',
      impactTitle: '对在用合同的影响',
      impactSummaryTpl: '{total} 份合同中 {affected} 份状态翻转',
      statusLabels: {
        standard: '合规',
        watch: '建议复审',
        sour: '需修订',
      },
      assetMetaLabel: '使用数据类别',
      insightTitle: '一次规则变更,合同全集传播',
      insightBody:
        '监管更新数据类别清单 — ontology 列出每份需修订的合同,自动附上条款修改草稿。法务团队不再 grep SharePoint,直接拿到一份分级工单。',
      assets: [
        { id: 'leg-c1', code: 'C-201', name: '欧盟客户数据处理方', h2sPpm: 220 },
        { id: 'leg-c2', code: 'C-218', name: '营销分析 DMP', h2sPpm: 180 },
        { id: 'leg-c3', code: 'C-244', name: '员工福利门户', h2sPpm: 160 },
        { id: 'leg-c4', code: 'C-261', name: 'HR 数据托管', h2sPpm: 140 },
        { id: 'leg-c5', code: 'C-272', name: '客服工具', h2sPpm: 120 },
        { id: 'leg-c6', code: 'C-288', name: 'KYC 文档库', h2sPpm: 100 },
        { id: 'leg-c7', code: 'C-301', name: '供应商门户', h2sPpm: 80 },
        { id: 'leg-c8', code: 'C-310', name: '采购系统', h2sPpm: 60 },
        { id: 'leg-c9', code: 'C-322', name: '公开营销站', h2sPpm: 40 },
        { id: 'leg-c10', code: 'C-331', name: '投资者关系站', h2sPpm: 20 },
      ],
      currentThresholdPpm: 100,
      thresholdRange: { min: 10, max: 250, step: 5 },
    },
    questionGrounded: {
      ...zhLabDemos.questionGrounded,
      insightTitle: '签或不签 — 当场给答案',
      insightBody:
        '法务审查过去是"3 天后回您"。现在每个条款都链到规则和先例,1 分钟内给签或不签。',
      questions: [
        {
          id: 'q-sign-contract',
          question: '今天能签 Acme MSA 吗?',
          answer:
            '这份 MSA 是 [[entity:e1|5 年期、年额 $800 万]] 的主协议。整期金额超 [[entity:e2|$1000 万 VP 阈值]],按 [[entity:e3|R-SIG-AUTH-01]] 需 [[entity:e4|VP 签字]]。[[entity:e5|责任条款 §11.4]] 为 2× 年额 + IP 不封顶 — 模板标准条款。VP 已收到 DocuSign 信封,签完即可。',
          entities: [
            { id: 'e1', label: '5 年期、年额 $800 万', kind: 'object', detail: '总额 $4000 万 · 自动续约 · 90 天提前通知' },
            { id: 'e2', label: '$1000 万 VP 阈值', kind: 'inference', detail: '汇率换算后权限上限 · 每季度复审' },
            { id: 'e3', label: 'R-SIG-AUTH-01', kind: 'constraint', detail: '≥ $1000 万总额需 VP 签字 + 多年合同还需告知董事会', citation: '集团 PA-2024-117 §3.1' },
            { id: 'e4', label: 'VP 签字', kind: 'action', detail: '在途 DocuSign 信封 · VP 队列 · 2 个工作日 SLA' },
            { id: 'e5', label: '责任条款 §11.4', kind: 'object', detail: '2× 年额 · IP 侵权不封顶 · 模板标准' },
          ],
        },
        {
          id: 'q-jurisdiction',
          question: '这条条款适用哪些司法管辖?',
          answer:
            '§17 (不可抗力) [[entity:e1|适用特拉华州法]],但合同有 [[entity:e2|APAC 例外 (§17.4)]] — 新加坡 / 香港 / 日本发生的事件适用当地法。[[entity:e3|冲突规则 R-CONFL-LAW-02]] 在两法重叠时按更严格的那一方解释。[[entity:e4|建议路径]]: 亚太事件经区域 GC 办公室处理。',
          entities: [
            { id: 'e1', label: '适用特拉华州法', kind: 'constraint', detail: '§22 主选准据法 · 专属管辖在威尔明顿' },
            { id: 'e2', label: 'APAC 例外 (§17.4)', kind: 'object', detail: 'SG/HK/JP 事件适用当地法,优先于 §17' },
            { id: 'e3', label: 'R-CONFL-LAW-02', kind: 'constraint', detail: '管辖重叠时按更严格的法律解释', citation: '法务手册 §4.2' },
            { id: 'e4', label: '建议路径', kind: 'action', detail: 'APAC 事件经区域 GC · 5 天 SLA' },
          ],
        },
        {
          id: 'q-gdpr-export',
          question: '把客户数据出口到 APAC 合规吗?',
          answer:
            '出口走 [[entity:e1|EU SCC 2021 + 英国附录]]。APAC 不具备充分性认定,需额外依赖 [[entity:e2|R-TIA-2023-04]] (传输影响评估,2025-Q1 复审)。[[entity:e3|次级处理方]] 3 家,均在 DPA 列明。[[entity:e4|待办缺口]]: 新加坡 PDPA 通知更新 2 个客户群 — 排在下季度。',
          entities: [
            { id: 'e1', label: 'EU SCC 2021 + 英国附录', kind: 'object', detail: 'Module 2 (控制者→处理方) · 2024-03 签 · 2 年复审周期' },
            { id: 'e2', label: 'R-TIA-2023-04', kind: 'constraint', detail: '传输影响评估年度复审 · APAC 评级低风险', citation: '隐私手册 §6' },
            { id: 'e3', label: '次级处理方', kind: 'object', detail: '3 家: 2 云 (AWS, Azure), 1 支持 (Acme APAC)' },
            { id: 'e4', label: '待办缺口', kind: 'action', detail: 'PDPA 通知更新 · 2 个客户群 · 排 2026-Q3' },
          ],
        },
      ],
    },
  },
}

const automotive: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...zhLabDemos.eventDriven,
      pendingLabel: '产线告警',
      pendingHint: '一台焊接机器人的振动异常。点 fire,看 ontology 从传感器 → 缺陷规则 → 质量响应一条链跑下来。',
      event: {
        code: '告警 V-7188',
        meta: '14:22 · 二厂 · 9 工位 · 机器人 R-031',
        summary: '焊接机器人振动激增',
        descriptor: '主轴振动 4.2 mm/s → 11.8 mm/s · 12 秒内 +180%',
        payloadLabel: 'PLC 遥测流',
        triggerNodeLabel: 'Event::ALERT_V-7188',
        triggerNodeDetail: 'PLC 传感器 RT-VIB-031 · 严重级 B · 14:22:08 · 趋势升级',
      },
      goals: [
        { id: 'compliance', label: '质量协议' },
        { id: 'uptime', label: '保产线不停' },
        { id: 'cost', label: '缺陷成本' },
      ],
      goalSelectorHint: '同一次振动激增,目标不同 → 响应链完全不同。',
      insightTitle: '在下一批 60 台车之前抓住缺陷',
      insightBody:
        '没有链,产线照跑,缺陷在终检环节才暴露 — 60 台车 + 4 小时返工。有 ontology,一秒钟内链就成型:缺陷类型、召回规则、下游工位通知、工单草稿。',
    },
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: 'ECU 固件补丁 (严重度阈值)',
      controlHint:
        '拖动阈值设置补丁严重度。Ontology 按新阈值重新分类全部 VIN 并标出召回范围。',
      thresholdLabel: '严重度',
      unitLabel: 'CVSS',
      impactTitle: '对全 VIN 基数的影响',
      impactSummaryTpl: '{total} 组 VIN 中 {affected} 组变更状态',
      statusLabels: {
        standard: '无需动作',
        watch: 'OTA 推送',
        sour: '召回回站',
      },
      assetMetaLabel: '受影响严重度分',
      insightTitle: '一次固件变更,全车队传播',
      insightBody:
        'ECU 固件 CVSS 更新落地 — ontology 把它映射到 ECU SKU,SKU 到车型年份,年份到 VIN,VIN 到经销商,经销商到服务能力。过去 3 周的召回规划项目,现在秒内出工单。',
      assets: [
        { id: 'auto-c1', code: 'COHORT-A', name: 'Model X 2023 · ECU-v2.1', h2sPpm: 9 },
        { id: 'auto-c2', code: 'COHORT-B', name: 'Model X 2022 · ECU-v2.0', h2sPpm: 8 },
        { id: 'auto-c3', code: 'COHORT-C', name: 'Model Y 2023 · ECU-v2.1', h2sPpm: 7 },
        { id: 'auto-c4', code: 'COHORT-D', name: 'Model Y 2022 · ECU-v1.9', h2sPpm: 6 },
        { id: 'auto-c5', code: 'COHORT-E', name: 'Model Z 2024 · ECU-v3.0', h2sPpm: 5 },
        { id: 'auto-c6', code: 'COHORT-F', name: 'Model Z 2023 · ECU-v2.8', h2sPpm: 4 },
        { id: 'auto-c7', code: 'COHORT-G', name: '卡车 T 2024 · ECU-v3.0', h2sPpm: 3 },
        { id: 'auto-c8', code: 'COHORT-H', name: '卡车 T 2023 · ECU-v2.9', h2sPpm: 2 },
        { id: 'auto-c9', code: 'COHORT-I', name: '厢货 V 2024 · ECU-v3.0', h2sPpm: 1 },
        { id: 'auto-c10', code: 'COHORT-J', name: '厢货 V 2023 · ECU-v2.8', h2sPpm: 1 },
      ],
      currentThresholdPpm: 5,
      thresholdRange: { min: 1, max: 10, step: 1 },
    },
    whatIf: {
      ...zhLabDemos.whatIf,
      insightTitle: '电池衰减,前向推演',
      insightBody:
        '每台 EV 的电池都有传感器派生的健康状态。Ontology 在不同使用场景下推演衰减曲线,曲线一旦穿越保修阈值就把对应批次翻成"售后可索赔" — 经销商在店里看到之前几个月就预警。',
      scenarios: [
        { id: 'normal', title: '城市/高速混合', description: '基准使用画像 · 恒温车库', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: '高强度快充', description: '每天 DC 快充 · 城市短循环', h2sGrowthPctPerMonth: 3 },
        { id: 'extreme', title: '高温地区车队', description: '户外停车 · 环境均温 ≥ 35 °C · 一天两次充电', h2sGrowthPctPerMonth: 5 },
      ],
      baseThresholdPpm: 80,
      assets: [
        { id: 'auto-b1', code: 'BATCH-1', name: '2022-Q1 电芯批 · 1.2 万组', h2sPpmStart: 95 },
        { id: 'auto-b2', code: 'BATCH-2', name: '2022-Q2 电芯批 · 1.8 万组', h2sPpmStart: 92 },
        { id: 'auto-b3', code: 'BATCH-3', name: '2022-Q3 电芯批 · 1.4 万组', h2sPpmStart: 88 },
        { id: 'auto-b4', code: 'BATCH-4', name: '2022-Q4 电芯批 · 1.6 万组', h2sPpmStart: 85 },
        { id: 'auto-b5', code: 'BATCH-5', name: '2023-Q1 电芯批 · 2.1 万组', h2sPpmStart: 90 },
        { id: 'auto-b6', code: 'BATCH-6', name: '2023-Q2 电芯批 · 2.0 万组', h2sPpmStart: 87 },
        { id: 'auto-b7', code: 'BATCH-7', name: '2023-Q3 电芯批 · 1.9 万组', h2sPpmStart: 84 },
        { id: 'auto-b8', code: 'BATCH-8', name: '2023-Q4 电芯批 · 2.2 万组', h2sPpmStart: 82 },
      ],
    },
  },
}

const logistics: IndustryDemoPack = {
  demos: {
    eventDriven: {
      ...zhLabDemos.eventDriven,
      pendingLabel: '运输异常',
      pendingHint: '一个集装箱在港口被扣。点 fire,看 ontology 现场合成绕行链。',
      event: {
        code: 'EVT SH-4421',
        meta: '06:08 · 新加坡港 · MSCU-7841',
        summary: '集装箱海关扣留 · 冷链货物',
        descriptor: '扣留原因 · 单证不符 · 预计放行 36-48h',
        payloadLabel: 'TMS 事件流',
        triggerNodeLabel: 'Event::SH_4421',
        triggerNodeDetail: 'TMS 适配器 MSC-EDI · 严重级 A · 06:08:42 · 冷链优先',
      },
      goals: [
        { id: 'compliance', label: '保冷链' },
        { id: 'uptime', label: '兑现客户 SLA' },
        { id: 'cost', label: '滞港费最小化' },
      ],
      goalSelectorHint: '同一次扣留,目标不同 → 复原计划完全不同。',
      insightTitle: '搬货,不是搬邮件',
      insightBody:
        '运营过去要在承运商、报关行、仓库、客服之间转发 6 封邮件。Ontology 几秒内把链合成出来 — 扣留原因、SLA 影响、替代路由、客户沟通草稿 — 调度只确认。',
    },
    multiAgent: {
      ...zhLabDemos.multiAgent,
      incidentLabel: '活动事件',
      incidentTitle: '订单 #ORD-88241 — 多段履约',
      incidentDetail:
        '3 个 SKU · 2 家承运商 · 跨境 (SG → HK → CN) · 48 小时承诺 · 含 1 段冷链。',
      injectButton: '注入订单',
      insightTitle: '五个 Agent,一个计划,全程审计链',
      insightBody:
        '挑选承运商、报关、仓储、最后一公里、对外沟通 — 每个都是独立 Agent,把决策投到 ontology 上。事后做 SLA 复盘,链路天然在那里,不用翻 Slack。',
      agents: [
        { id: 'a-carrier', icon: '🚚', name: '承运商选择', accent: 'amber' },
        { id: 'a-broker', icon: '🛃', name: '报关', accent: 'cyan' },
        { id: 'a-warehouse', icon: '🏬', name: '仓储', accent: 'violet' },
        { id: 'a-lastmile', icon: '🛵', name: '最后一公里', accent: 'pass' },
        { id: 'a-comms', icon: '📣', name: '客户沟通', accent: 'info' },
      ],
    },
    whatIf: {
      ...zhLabDemos.whatIf,
      insightTitle: '压力测试 SLA,不是表格',
      insightBody:
        '港口拥堵、油价冲击、台风窗口都作为情景参数注入 ontology。SLA 承诺按线路逐条重投,风险线路自带缓解方案 — 客户没投诉之前就摆在调度桌上。',
      scenarios: [
        { id: 'normal', title: '基线运营', description: '当前排班 · 港口正常吞吐', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: '港口拥堵 +30%', description: '新加坡 + 长滩拥堵积压', h2sGrowthPctPerMonth: 4 },
        { id: 'extreme', title: '台风窗口', description: '年度台风 · 港口关闭 7 天', h2sGrowthPctPerMonth: 7 },
      ],
      baseThresholdPpm: 100,
      assets: [
        { id: 'log-l1', code: 'LANE-1', name: 'SG → CN 跨境', h2sPpmStart: 110 },
        { id: 'log-l2', code: 'LANE-2', name: 'HK → JP 空运', h2sPpmStart: 95 },
        { id: 'log-l3', code: 'LANE-3', name: '美西 → 欧洲海运', h2sPpmStart: 88 },
        { id: 'log-l4', code: 'LANE-4', name: '欧洲内陆公路', h2sPpmStart: 78 },
        { id: 'log-l5', code: 'LANE-5', name: 'APAC 末端配送', h2sPpmStart: 72 },
        { id: 'log-l6', code: 'LANE-6', name: '北美当日达', h2sPpmStart: 65 },
        { id: 'log-l7', code: 'LANE-7', name: '拉美境内', h2sPpmStart: 60 },
        { id: 'log-l8', code: 'LANE-8', name: '澳洲境内', h2sPpmStart: 55 },
      ],
    },
  },
}

const government: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: '区划政策严格度 (容积率上限)',
      controlHint:
        '拖动设置新的容积率 (FAR) 上限。Ontology 重新评估每个在审许可证并标出突破新规的项目。',
      thresholdLabel: '容积率上限',
      unitLabel: 'FAR',
      impactTitle: '对在审许可证的影响',
      impactSummaryTpl: '{total} 份许可证中 {affected} 份变更状态',
      statusLabels: {
        standard: '合规',
        watch: '建议申请变更',
        sour: '需重新提交',
      },
      assetMetaLabel: '许可证容积率 (×100)',
      insightTitle: '一次规则变更,全许可证传播',
      insightBody:
        '议会更新区划法规 — ontology 标出每个受影响许可证,变更路径自动起草。市民在门户上看到清晰的前后对比,议员在会议厅看到影响面,审计日志天生在那里。',
      assets: [
        { id: 'gov-p1', code: 'PRM-2201', name: '滨江综合体 A 塔', h2sPpm: 480 },
        { id: 'gov-p2', code: 'PRM-2233', name: '市中心写字楼扩建', h2sPpm: 420 },
        { id: 'gov-p3', code: 'PRM-2287', name: '物流园 · 二期', h2sPpm: 380 },
        { id: 'gov-p4', code: 'PRM-2305', name: '中高层住宅小区', h2sPpm: 340 },
        { id: 'gov-p5', code: 'PRM-2348', name: '学校扩建', h2sPpm: 300 },
        { id: 'gov-p6', code: 'PRM-2399', name: '社区中心', h2sPpm: 260 },
        { id: 'gov-p7', code: 'PRM-2412', name: '本地商业街', h2sPpm: 220 },
        { id: 'gov-p8', code: 'PRM-2455', name: '独栋住宅填空', h2sPpm: 180 },
        { id: 'gov-p9', code: 'PRM-2478', name: '历史建筑改造', h2sPpm: 140 },
        { id: 'gov-p10', code: 'PRM-2491', name: '公园便利亭', h2sPpm: 100 },
      ],
      currentThresholdPpm: 350,
      thresholdRange: { min: 50, max: 600, step: 25 },
    },
    continuousAudit: {
      ...zhLabDemos.continuousAudit,
      insightTitle: '审计是心跳,不是项目',
      insightBody:
        '公共服务 KPI、许可证周期时间、采购供应商核查都在 ontology 里持续跑。审计师不再一年抽样一次,每天早上拿到一份活的、已分类的待办清单。',
    },
    multiAgent: {
      ...zhLabDemos.multiAgent,
      incidentLabel: '活动事件',
      incidentTitle: 'INC-MULTI-2106 — 多部门联合响应',
      incidentDetail:
        '工业区起火靠近住宅 · 需要疏散 · 公交改线 · 医院分诊预警。',
      injectButton: '触发演练',
      insightTitle: '五个部门,一条链,不用转椅子办公',
      insightBody:
        '消防、警察、公交、卫生、应急指挥各自在共享 ontology 上投递。事后复盘 — 谁、什么时候、基于什么证据做了什么决定 — 链路天然在那里。',
      agents: [
        { id: 'a-fire', icon: '🚒', name: '消防', accent: 'amber' },
        { id: 'a-police', icon: '🚓', name: '警察', accent: 'cyan' },
        { id: 'a-transit', icon: '🚇', name: '公交', accent: 'violet' },
        { id: 'a-health', icon: '🏥', name: '卫生/急救', accent: 'pass' },
        { id: 'a-eoc', icon: '🛰', name: '应急指挥', accent: 'info' },
      ],
    },
  },
}

const insurance: IndustryDemoPack = {
  demos: {
    documentDriven: {
      ...zhLabDemos.documentDriven,
      documentsLabel: '理赔来件',
      documentsHint: '点任意文件去"摄入",看 LLM 抽取实体,ontology 把它绑到保单和规则,理赔链合成出来。',
      insightTitle: '出险首报 → 几秒钟出承保结论',
      insightBody:
        '事故报告、维修估价、首报表都进同一个 ontology。条款、免赔额、除外责任、历史出险史在摄入时就绑好了 — 理赔员看到的是已分类的决策,不是证据堆。',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'FNOL-2104-P12.pdf',
          meta: '3 页 · 理赔门户上传',
          summary: '车辆碰撞 · 单车事故 · 物损',
          entities: [
            { kind: '保单', value: 'AUTO-220914-P12' },
            { kind: '被保人', value: '帕特尔先生 · 6 年无出险记录' },
            { kind: '出险日期', value: '2026-05-12' },
            { kind: '原因', value: '湿滑路面打滑 · 单车' },
            { kind: '估损', value: '前翼子板 $4,800' },
          ],
        },
        {
          id: 'rfq',
          icon: '👮',
          name: 'PoliceReport-NCT-7841.pdf',
          meta: '5 页 · 理赔员上传',
          summary: '警方报告 · 无传票 · 无第三方',
          entities: [
            { kind: '出警警官', value: '警号 4112 · 现场记录' },
            { kind: '路况', value: '湿滑路面 · 黄昏' },
            { kind: '传票', value: '无' },
            { kind: '证人', value: '0 名' },
            { kind: '风险', value: '无酒驾 · 无欺诈嫌疑' },
          ],
        },
        {
          id: 'incident',
          icon: '🔧',
          name: 'RepairEstimate-RE-9023.pdf',
          meta: '2 页 · 认证修理厂上传',
          summary: '钣金估价 · 原厂件',
          entities: [
            { kind: '修理厂', value: 'NCT 认证钣金 · 一级' },
            { kind: '配件', value: '原厂保险杠 · 原厂大灯' },
            { kind: '工时', value: '12 小时 · $95/小时' },
            { kind: '总价', value: '$4,820 含喷漆' },
            { kind: '工期', value: '5 天 · 含代步车' },
          ],
        },
      ],
    },
    eventDriven: {
      ...zhLabDemos.eventDriven,
      pendingLabel: 'CAT 事件激增',
      pendingHint: '一次风暴触发的报案激增。点 fire,看 ontology 给 FNOL 分桶、路由理赔员、预备准备金。',
      event: {
        code: 'CAT-2614',
        meta: '03:18 · NCT 区域 · 风暴 Alma',
        summary: '+1840 份报案 · 是基线的 6 倍',
        descriptor: '风速 95 mph · 覆盖 4 县 · 报案峰值 +72 小时',
        payloadLabel: 'NWS + FNOL 流',
        triggerNodeLabel: 'Event::CAT_2614',
        triggerNodeDetail: 'NWS 接入 + 门户 · 严重级 A · 03:18:02 · 升级规则 R-CAT-01',
      },
      goals: [
        { id: 'compliance', label: '准备金 + 监管报告' },
        { id: 'uptime', label: '理赔员路由' },
        { id: 'cost', label: '欺诈分诊' },
      ],
      goalSelectorHint: '同一次 CAT 事件,目标不同 → 响应链完全不同。',
      insightTitle: 'CAT 响应,不用作战室',
      insightBody:
        'CAT 事件以前要拉一个 48 小时作战室:核保、理赔、再保、监管,各跑各的表格。Ontology 实时给报案路由、预备准备金、更新报监管的报告 — 不用转椅子。',
    },
    riskScoring: {
      ...zhLabDemos.riskScoring,
      controlLabel: '为商业物业 12 Lakeside Plaza 评分',
      controlHint:
        '调整三个风险维度。Ontology 把加权贡献聚合成单一核保评分,每个数字都能链回背后的规则和数据源。',
      dimensionsTitle: '核保维度',
      dimensions: [
        { id: 'financial', label: '灾害敞口', weightPct: 40, description: '洪水/野火/风灾区 + 历史损失', defaultValue: 5 },
        { id: 'operational', label: '物业状况', weightPct: 35, description: '建筑年龄、屋顶、消防设施、安保', defaultValue: 4 },
        { id: 'esg', label: '客户行为', weightPct: 25, description: '历史出险、付款记录、被保人画像', defaultValue: 3 },
      ],
      scoreTitle: '核保风险评分',
      insightTitle: '能自我解释的核保',
      insightBody:
        '过去经纪人收到的是"承保/拒保/转送",没有原因。现在评分能链回灾害地图、物业检验、历史出险 — 同一屏可起草批单。',
      contextSupplier: {
        label: '在核保的物业',
        name: '12 Lakeside Plaza · 商业综合体',
        detail: '1998 年建 · 4 层 · 配喷淋 · 标的总值 $1400 万',
      },
    },
  },
}

const retail: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: '品类毛利底线 (%)',
      controlHint:
        '拖动模拟新的毛利底线。Ontology 按新底线重新评估每个 SKU,标出需要调价或调整促销的品类。',
      thresholdLabel: '毛利底线',
      unitLabel: '%',
      impactTitle: '对在售 SKU 的影响',
      impactSummaryTpl: '{total} 个 SKU 组中 {affected} 个需调价',
      statusLabels: {
        standard: '在底线以上',
        watch: '复审促销',
        sour: '重定价',
      },
      assetMetaLabel: '当前毛利率 %',
      insightTitle: '一次毛利规则,全目录传播',
      insightBody:
        '过去毛利政策躺在 Excel 里,没人同步。Ontology 把每个 SKU 绑到成本、促销、品类规则 — 改一次底线,系统就标出谁要调价、谁退促销、谁要重谈供应商。',
      assets: [
        { id: 'ret-s1', code: 'CAT-A1', name: '高端乳品', h2sPpm: 32 },
        { id: 'ret-s2', code: 'CAT-A2', name: '零食棒', h2sPpm: 28 },
        { id: 'ret-s3', code: 'CAT-A3', name: '清洁品牌', h2sPpm: 25 },
        { id: 'ret-s4', code: 'CAT-A4', name: '冷冻即食', h2sPpm: 22 },
        { id: 'ret-s5', code: 'CAT-A5', name: '高端宠物食品', h2sPpm: 20 },
        { id: 'ret-s6', code: 'CAT-B1', name: '主流饮料', h2sPpm: 16 },
        { id: 'ret-s7', code: 'CAT-B2', name: '店内烘焙', h2sPpm: 14 },
        { id: 'ret-s8', code: 'CAT-B3', name: '自有品牌干货', h2sPpm: 12 },
        { id: 'ret-s9', code: 'CAT-B4', name: '果蔬基础品', h2sPpm: 10 },
        { id: 'ret-s10', code: 'CAT-B5', name: '大宗家用', h2sPpm: 8 },
      ],
      currentThresholdPpm: 18,
      thresholdRange: { min: 5, max: 40, step: 1 },
    },
    whatIf: {
      ...zhLabDemos.whatIf,
      insightTitle: '需求推演,按品类',
      insightBody:
        '促销规划过去是回望性的。Ontology 在不同情景下推演品类需求 — 促销节奏、天气、竞品入场 — 一旦前向曲线穿越安全线,就把品类翻成"缺货风险"。',
      scenarios: [
        { id: 'normal', title: '基线节奏', description: '当前促销排期 · 天气正常', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: '激进促销', description: '促销强度翻倍 · 会员推送', h2sGrowthPctPerMonth: 4 },
        { id: 'extreme', title: '热浪 + 竞品入场', description: '持续高温 + 3 个市场新增竞品', h2sGrowthPctPerMonth: 7 },
      ],
      baseThresholdPpm: 90,
      assets: [
        { id: 'ret-d1', code: 'CAT-1', name: '冷饮料', h2sPpmStart: 95 },
        { id: 'ret-d2', code: 'CAT-2', name: '冰淇淋', h2sPpmStart: 92 },
        { id: 'ret-d3', code: 'CAT-3', name: '咸味零食', h2sPpmStart: 88 },
        { id: 'ret-d4', code: 'CAT-4', name: '防晒护理', h2sPpmStart: 84 },
        { id: 'ret-d5', code: 'CAT-5', name: '户外烧烤', h2sPpmStart: 80 },
        { id: 'ret-d6', code: 'CAT-6', name: '烘焙', h2sPpmStart: 70 },
        { id: 'ret-d7', code: 'CAT-7', name: '冷冻', h2sPpmStart: 65 },
        { id: 'ret-d8', code: 'CAT-8', name: '乳品', h2sPpmStart: 60 },
      ],
    },
    continuousAudit: {
      ...zhLabDemos.continuousAudit,
      insightTitle: '损耗与异常,持续审计',
      insightBody:
        '过去 POS 审计是夜间批跑。Ontology 实时跑每条交易流,对照规则 — 退货异常、撤单、称重不符、礼品卡套路 — 把分类后的待办实时投给防损部门。',
    },
  },
}

const realestate: IndustryDemoPack = {
  demos: {
    changePropagation: {
      ...zhLabDemos.changePropagation,
      controlLabel: '基准利率 (基点)',
      controlHint:
        '拖动模拟基准利率变动。Ontology 按新的资金成本重新分类每个按揭分层,标出翻档的池子。',
      thresholdLabel: '利率',
      unitLabel: 'bps',
      impactTitle: '对按揭账面的影响',
      impactSummaryTpl: '{total} 个分层中 {affected} 个翻档',
      statusLabels: {
        standard: '在带宽内',
        watch: '收益预警',
        sour: '市值减值',
      },
      assetMetaLabel: '分层利率敏感度',
      insightTitle: '一次利率变动,全组合传播',
      insightBody:
        '司库过去要等 5 天才能完成账面重估。Ontology 把每个分层绑到对应物业、LTV、产品类型 — 利率一动,受影响池子和对冲动作同时出来。',
      assets: [
        { id: 'rre-t1', code: 'TRNCH-1', name: '30 年固定 · 优质', h2sPpm: 820 },
        { id: 'rre-t2', code: 'TRNCH-2', name: '15 年固定 · 优质', h2sPpm: 660 },
        { id: 'rre-t3', code: 'TRNCH-3', name: 'ARM 5/1', h2sPpm: 540 },
        { id: 'rre-t4', code: 'TRNCH-4', name: 'ARM 7/1', h2sPpm: 460 },
        { id: 'rre-t5', code: 'TRNCH-5', name: '大额固定', h2sPpm: 380 },
        { id: 'rre-t6', code: 'TRNCH-6', name: '商业按揭', h2sPpm: 300 },
        { id: 'rre-t7', code: 'TRNCH-7', name: '多户型', h2sPpm: 220 },
        { id: 'rre-t8', code: 'TRNCH-8', name: '反向按揭', h2sPpm: 160 },
        { id: 'rre-t9', code: 'TRNCH-9', name: '过桥贷', h2sPpm: 100 },
        { id: 'rre-t10', code: 'TRNCH-10', name: '建设贷', h2sPpm: 60 },
      ],
      currentThresholdPpm: 400,
      thresholdRange: { min: 50, max: 1000, step: 25 },
    },
    whatIf: {
      ...zhLabDemos.whatIf,
      insightTitle: '租金收益率,前向推演',
      insightBody:
        '物业测算过去躺在静态表格里。Ontology 在出租率、利率、资本开支不同情景下推演租金收益率 — 任何一栋大楼的前向收益率一旦跌破投资委托的下限,立刻预警。',
      scenarios: [
        { id: 'normal', title: '基线出租率', description: '当前租金 · 95% 出租率假设', h2sGrowthPctPerMonth: 1 },
        { id: 'heavy', title: '弱市', description: '出现免租优惠 · 出租率漂到 88%', h2sGrowthPctPerMonth: 3 },
        { id: 'extreme', title: '衰退 + 税改', description: '出租率 80% · 资本化率走阔 · 税改落地', h2sGrowthPctPerMonth: 6 },
      ],
      baseThresholdPpm: 85,
      assets: [
        { id: 'rre-b1', code: 'BLDG-1', name: 'A 塔 · 甲级写字楼', h2sPpmStart: 95 },
        { id: 'rre-b2', code: 'BLDG-2', name: 'B 塔 · 甲级写字楼', h2sPpmStart: 90 },
        { id: 'rre-b3', code: 'BLDG-3', name: '多户型 · 220 户', h2sPpmStart: 88 },
        { id: 'rre-b4', code: 'BLDG-4', name: '多户型 · 160 户', h2sPpmStart: 85 },
        { id: 'rre-b5', code: 'BLDG-5', name: '商业中心 A', h2sPpmStart: 82 },
        { id: 'rre-b6', code: 'BLDG-6', name: '商业中心 B', h2sPpmStart: 78 },
        { id: 'rre-b7', code: 'BLDG-7', name: '工业 · 物流', h2sPpmStart: 74 },
        { id: 'rre-b8', code: 'BLDG-8', name: '工业 · 轻型制造', h2sPpmStart: 70 },
      ],
    },
    riskScoring: {
      ...zhLabDemos.riskScoring,
      controlLabel: '为物业 220 Riverwalk Tower 评分',
      controlHint:
        '调整三个风险维度。Ontology 把加权贡献聚合成单一物业风险评分,每个数字都能链回背后的规则和数据源。',
      dimensionsTitle: '物业风险维度',
      dimensions: [
        { id: 'financial', label: '市场与资本化率', weightPct: 40, description: '资本化率 vs 基准、市场流动性、租金增长', defaultValue: 5 },
        { id: 'operational', label: '建筑状况', weightPct: 35, description: '年龄、资本开支积压、ESG 改造需求', defaultValue: 4 },
        { id: 'esg', label: '租户结构与租约', weightPct: 25, description: '租户信用、租约到期墙、集中度', defaultValue: 3 },
      ],
      scoreTitle: '物业风险评分',
      insightTitle: '投资委员会,不是投资故事',
      insightBody:
        'IC 备忘过去是 50 页 PDF。有 ontology,委员会一屏看到评分、推理链、可比交易、敏感性情景 — 30 分钟过会,不是 3 周。',
      contextSupplier: {
        label: '在评估的物业',
        name: '220 Riverwalk Tower',
        detail: '甲级写字楼 · 32 层 · 目标收购价 $4.8 亿',
      },
    },
  },
}

const education: IndustryDemoPack = {
  demos: {
    goalDriven: {
      ...zhLabDemos.goalDriven,
      wishesLabel: '学生愿望',
      wishesHint: '选一个愿望,看 ontology 把学术计划合成 — 课程、先修、助学金、辅导 — 成一条路径。',
      insightTitle: '同一愿望,三种计划,一个 ontology',
      insightBody:
        '过去导师辅导要三次会议加一张表。Ontology 几秒钟内合成路径 — 专业要求、先修、转学分、助学金、导师档期 — 从学生的一句愿望出发。',
      wishes: [
        { id: 'w1', goalId: 'compliance', quote: '我想在 2027 年春季前拿到 CS 学位毕业。', speaker: '艾沙,大三' },
        { id: 'w2', goalId: 'uptime', quote: '我想从生物转数据科学,但不损失一学期。', speaker: '马可,大二' },
        { id: 'w3', goalId: 'cost', quote: '我需要一条暑期能保住助学金资格的路径。', speaker: '普里娅,大一' },
      ],
    },
    documentDriven: {
      ...zhLabDemos.documentDriven,
      documentsLabel: '申请来件',
      documentsHint: '点任意文件去"摄入",看 LLM 抽取实体,ontology 把它绑到项目,招生链合成。',
      insightTitle: '三份文件,一条申请链',
      insightBody:
        '成绩单、推荐信、考试分都进同一个 ontology — 不同文件类型触发不同审阅链。项目方看到的是统一的申请人视图,不是一堆 PDF。',
      documents: [
        {
          id: 'contract',
          icon: '📄',
          name: 'Transcript-Patel-2024.pdf',
          meta: '4 页 · 申请人上传',
          summary: '本科成绩单 · Lakeside 大学',
          entities: [
            { kind: '申请人', value: '帕特尔 A · ID 2024-11824' },
            { kind: '专业', value: '生物 · 辅修统计' },
            { kind: 'GPA', value: '3.78 / 4.0' },
            { kind: '亮点', value: '荣誉毕业论文 · 2 篇发表' },
            { kind: '转学分', value: 'CS 可转 6 学分' },
          ],
        },
        {
          id: 'rfq',
          icon: '✉',
          name: 'Recommendation-Prof-Lee.pdf',
          meta: '2 页 · 推荐人上传',
          summary: '强推荐 · 研究导师',
          entities: [
            { kind: '推荐人', value: 'Lee 教授 · Lakeside 生物' },
            { kind: '关系', value: '2 年研究导师' },
            { kind: '评级', value: '所带学生前 5%' },
            { kind: '优势', value: '研究方法 · 独立性' },
            { kind: '备注', value: '支持转 CS' },
          ],
        },
        {
          id: 'incident',
          icon: '📊',
          name: 'GRE-Score-Report.pdf',
          meta: '1 页 · ETS 上传',
          summary: '标准化考试分 · GRE',
          entities: [
            { kind: '考试', value: 'GRE General · 2024-10' },
            { kind: '语文', value: '165 / 170 · 95% 位次' },
            { kind: '数学', value: '168 / 170 · 92% 位次' },
            { kind: '写作', value: '5.0 / 6.0' },
            { kind: '有效期', value: '2029 年前有效' },
          ],
        },
      ],
    },
    continuousAudit: {
      ...zhLabDemos.continuousAudit,
      insightTitle: '认证作为心跳',
      insightBody:
        '过去认证检查意味着 6 个月的突击。Ontology 持续对照标准评估专业 — 课程覆盖、师资资质、评估循环 — 漂移当天就标出来,不是评审年才发现。',
    },
  },
}

export const zhIndustryDemos = {
  energy,
  finance,
  healthcare,
  legal,
  automotive,
  logistics,
  government,
  insurance,
  retail,
  realestate,
  education,
} as const
